import PropTypes from 'prop-types'

/**
 * NextStepsPanel - Visual icon-based navigation for post-assessment actions
 */
function NextStepsPanel({
  taskCount = 0,
  isGenerating = false,
  onViewTasks,
  onViewWhiteboard,
  onAskOrin,
  onDownloadReport,
  onProgressToCleo,
  variant = 'compact'
}) {
  const items = [
    {
      id: 'tasks',
      icon: isGenerating ? (
        <div className="w-8 h-8 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      label: isGenerating ? 'Creating your action items...' : taskCount > 0 ? `Check out my ${taskCount} recommended actions` : 'Check out my recommended actions',
      onClick: onViewTasks,
      color: 'green',
      badge: !isGenerating && taskCount > 0 ? taskCount : null
    },
    {
      id: 'whiteboard',
      icon: isGenerating ? (
        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      label: isGenerating ? 'Mapping your insights...' : 'Explore my visual breakdown on the whiteboard',
      onClick: onViewWhiteboard,
      color: 'purple'
    },
    {
      id: 'ask',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      label: 'Ask me anything about this assessment',
      onClick: onAskOrin,
      color: 'amber'
    },
    {
      id: 'report',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      label: 'Download PDF assessment report',
      onClick: onDownloadReport,
      color: 'cyan'
    },
    {
      id: 'cleo',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      ),
      label: 'Progress to Go-to-Market Strategy',
      onClick: onProgressToCleo,
      color: 'pink'
    }
  ]

  const colorClasses = {
    cyan: {
      bg: 'bg-cyan-900/30',
      bgHover: 'hover:bg-cyan-800/40',
      border: 'border-cyan-700/50',
      borderHover: 'hover:border-cyan-500/70',
      icon: 'text-cyan-400',
      iconHover: 'group-hover:text-cyan-300',
      text: 'text-cyan-300',
      textHover: 'group-hover:text-cyan-200'
    },
    green: {
      bg: 'bg-green-900/30',
      bgHover: 'hover:bg-green-800/40',
      border: 'border-green-700/50',
      borderHover: 'hover:border-green-500/70',
      icon: 'text-green-400',
      iconHover: 'group-hover:text-green-300',
      text: 'text-green-300',
      textHover: 'group-hover:text-green-200'
    },
    purple: {
      bg: 'bg-purple-900/30',
      bgHover: 'hover:bg-purple-800/40',
      border: 'border-purple-700/50',
      borderHover: 'hover:border-purple-500/70',
      icon: 'text-purple-400',
      iconHover: 'group-hover:text-purple-300',
      text: 'text-purple-300',
      textHover: 'group-hover:text-purple-200'
    },
    amber: {
      bg: 'bg-amber-900/30',
      bgHover: 'hover:bg-amber-800/40',
      border: 'border-amber-700/50',
      borderHover: 'hover:border-amber-500/70',
      icon: 'text-amber-400',
      iconHover: 'group-hover:text-amber-300',
      text: 'text-amber-300',
      textHover: 'group-hover:text-amber-200'
    },
    pink: {
      bg: 'bg-pink-900/30',
      bgHover: 'hover:bg-pink-800/40',
      border: 'border-pink-700/50',
      borderHover: 'hover:border-pink-500/70',
      icon: 'text-pink-400',
      iconHover: 'group-hover:text-pink-300',
      text: 'text-pink-300',
      textHover: 'group-hover:text-pink-200'
    }
  }

  if (variant === 'compact') {
    return (
      <div className="grid grid-cols-5 gap-2">
        {items.map((item) => {
          const colors = colorClasses[item.color]
          return (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`group flex flex-col items-center p-3 rounded-xl ${colors.bg} ${colors.bgHover} border ${colors.border} ${colors.borderHover} transition-all`}
            >
              <div className={`${colors.icon} ${colors.iconHover} transition-colors mb-1`}>
                {item.icon}
              </div>
              <span className={`text-xs font-medium ${colors.text} ${colors.textHover} transition-colors text-center`}>
                {item.label}
              </span>
            </button>
          )
        })}
      </div>
    )
  }

  if (variant === 'minimal') {
    // Short action text for each item
    const getActionText = (item) => {
      if (item.id === 'tasks') {
        if (isGenerating) return 'Creating tasks...'
        return taskCount > 0 ? `Check out ${taskCount} task${taskCount > 1 ? 's' : ''} I created` : 'View recommended tasks'
      }
      if (item.id === 'whiteboard') {
        if (isGenerating) return 'Building whiteboard...'
        return 'Explore visual breakdown'
      }
      if (item.id === 'ask') return 'Ask me anything'
      if (item.id === 'report') return 'Download PDF report'
      if (item.id === 'cleo') return 'Progress to Go-to-Market Strategy'
      return item.label
    }

    const getTitle = (item) => {
      if (item.id === 'tasks') return 'Tasks'
      if (item.id === 'whiteboard') return 'Whiteboard'
      if (item.id === 'ask') return 'Ask Orin'
      if (item.id === 'report') return 'Report'
      if (item.id === 'cleo') return 'Cleo'
      return item.id
    }

    return (
      <>
        {/* Mobile: Vertical slim rows */}
        <div className="flex flex-col gap-2 md:hidden">
          {items.map((item) => {
            const colors = colorClasses[item.color]
            return (
              <button
                key={item.id}
                onClick={item.onClick}
                className="group flex items-center gap-3 rounded-lg px-4 py-3 border bg-gray-800 border-gray-700 hover:border-gray-600 transition-all"
                title={item.label}
              >
                <div className={`${colors.icon} ${colors.iconHover} transition-colors flex-shrink-0`}>
                  <div className="w-6 h-6">{item.icon}</div>
                </div>
                <div className="flex-1 text-left">
                  <div className={`text-sm font-medium ${colors.text}`}>
                    {getTitle(item)}
                  </div>
                  <div className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
                    {getActionText(item)}
                  </div>
                </div>
                <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-400 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )
          })}
        </div>

        {/* Desktop: Grid layout */}
        <div className="hidden md:grid grid-cols-5 gap-4">
          {items.map((item) => {
            const colors = colorClasses[item.color]
            return (
              <button
                key={item.id}
                onClick={item.onClick}
                className="group rounded-lg p-4 text-center border bg-gray-800 border-gray-700 hover:border-gray-600 transition-all flex flex-col"
                title={item.label}
              >
                <div className={`${colors.icon} ${colors.iconHover} transition-colors flex justify-center mb-2`}>
                  {item.icon}
                </div>
                <div className="flex-1 flex flex-col justify-start">
                  <div className={`text-sm font-medium ${colors.text} mb-1`}>
                    {getTitle(item)}
                  </div>
                  <div className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
                    {getActionText(item)}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </>
    )
  }

  // Default variant - cards with labels
  return (
    <div className="grid grid-cols-5 gap-3">
      {items.map((item) => {
        const colors = colorClasses[item.color]
        return (
          <button
            key={item.id}
            onClick={item.onClick}
            className={`group relative flex flex-col items-center p-4 rounded-xl ${colors.bg} ${colors.bgHover} border ${colors.border} ${colors.borderHover} transition-all`}
          >
            {item.badge && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {item.badge}
              </span>
            )}
            <div className={`${colors.icon} ${colors.iconHover} transition-colors mb-2`}>
              {item.icon}
            </div>
            <span className={`text-xs font-medium ${colors.text} ${colors.textHover} transition-colors text-center leading-tight`}>
              {item.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}

NextStepsPanel.propTypes = {
  taskCount: PropTypes.number,
  isGenerating: PropTypes.bool,
  onViewTasks: PropTypes.func,
  onViewWhiteboard: PropTypes.func,
  onAskOrin: PropTypes.func,
  onDownloadReport: PropTypes.func,
  onProgressToCleo: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'compact', 'minimal'])
}

export default NextStepsPanel
